import { useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import type { MonthlyRecapsArticle } from '@/features/monthly-recaps';

import { MonthlyRecapsArticleTeaser } from './MonthlyRecapsArticleTeaser';

const ROW_GAP = 16;
const ESTIMATED_ROW_HEIGHT = 140;
const OVERSCAN = 5;
const LAST_ROW_MARGIN_BOTTOM = 16;

type MonthlyRecapsListViewProps = {
  articles: MonthlyRecapsArticle[];
  onSelectArticle: (article: MonthlyRecapsArticle) => void;
};

export const MonthlyRecapsListView = ({
  articles,
  onSelectArticle
}: MonthlyRecapsListViewProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: articles.length,
    gap: ROW_GAP,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
    getItemKey: index => articles[index]?.file ?? index
  });

  return (
    <div
      ref={scrollRef}
      className='min-h-0 flex-1 overflow-y-auto px-4'
    >
      <div
        className='relative w-full'
        style={{
          height: articles.length ? rowVirtualizer.getTotalSize() + LAST_ROW_MARGIN_BOTTOM : 0
        }}
      >
        {rowVirtualizer.getVirtualItems().map(virtualRow => {
          const article = articles[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              ref={rowVirtualizer.measureElement}
              className='absolute top-0 left-0 w-full'
              data-index={virtualRow.index}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              <MonthlyRecapsArticleTeaser
                article={article}
                onClick={() => {
                  onSelectArticle(article);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
